import { getConfig } from '@/config/env';

import { ApiError, errorKeyForSuiteApiResult } from './errors';
import { PortalClient } from './portal-client';
import type { Credentials, PortalUser } from './types';
import { ESuiteApiResult } from './types';

export interface PasswordResetMailTemplate {
  subject: string;
  body: string;
}

interface PasswordResetRequestBody {
  email: string;
  mailTemplate: PasswordResetMailTemplate;
}

interface PasswordSetRequestBody {
  email: string;
  resetToken: string;
  newPassword: string;
}

/**
 * Authentication API — thin, typed wrappers over the SuiteAPI user endpoints.
 *
 * Login/logout use the SuiteAPI header authentication contract:
 *   PUT /SuiteAPI/User/LoginV1?moduleKey=MGR
 *   PUT /SuiteAPI/User/LogoutV1
 */
export class AuthApi {
  private readonly client: PortalClient;
  private readonly moduleKey: string;

  public constructor(client: PortalClient) {
    this.client = client;
    this.moduleKey = getConfig().moduleKey;
  }

  /**
   * Authenticates against the real SuiteAPI.
   *
   * @returns the authenticated {@link PortalUser} on success.
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async login(credentials: Credentials, signal?: AbortSignal): Promise<PortalUser> {
    // Credentials must be attached to the request that validates them.
    this.client.setCredentials(credentials);

    let result;
    try {
      result = await this.client.request<PortalUser>({
        method: 'PUT',
        path: '/SuiteAPI/User/LoginV1',
        query: { moduleKey: this.moduleKey },
        authenticated: true,
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      // Roll back the tentative credentials if the request itself failed.
      this.client.setCredentials(null);
      throw error;
    }

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success) {
      this.client.setCredentials(null);
      throw new ApiError('unauthorized', errorKeyForSuiteApiResult(serverResult), {
        serverResult,
      });
    }

    return result.data ?? {};
  }

  /**
   * Re-validates stored credentials against the Portal (used on app start /
   * page refresh). Returns the fresh user, or null when the session is invalid.
   */
  public async restore(credentials: Credentials, signal?: AbortSignal): Promise<PortalUser | null> {
    try {
      return await this.login(credentials, signal);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Ends the Portal session. Best-effort: local state is cleared regardless of
   * the server outcome so the user is always signed out client-side.
   */
  public async logout(credentials: Credentials, signal?: AbortSignal): Promise<void> {
    this.client.setCredentials(credentials);
    try {
      await this.client.request({
        method: 'PUT',
        path: '/SuiteAPI/User/LogoutV1',
        authenticated: true,
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      // A failed logout must not trap the user in an authenticated shell.
      if (!(error instanceof ApiError) || error.kind === 'unknown') {
        throw error;
      }
    } finally {
      this.client.setCredentials(null);
    }
  }

  /**
   * Requests a password-reset mail from the Portal.
   *
   * SECURITY: an unknown e-mail address is treated like success so the client
   * does not reveal whether an account exists for a given address.
   */
  public async requestPasswordReset(
    email: string,
    mailTemplate: PasswordResetMailTemplate,
    signal?: AbortSignal,
  ): Promise<void> {
    const body: PasswordResetRequestBody = {
      email,
      mailTemplate,
    };

    const result = await this.client.request<string>({
      method: 'PUT',
      path: '/SuiteAPI/User/PasswordResetV1',
      body,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult === ESuiteApiResult.Success || serverResult === ESuiteApiResult.InvalidLogin) {
      return;
    }

    throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), {
      serverResult,
    });
  }

  /**
   * Applies a new password using the reset token from the password-reset mail.
   */
  public async resetPassword(
    email: string,
    resetToken: string,
    newPassword: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const body: PasswordSetRequestBody = {
      email,
      resetToken,
      newPassword,
    };

    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/User/PasswordSetV1',
      body,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult === ESuiteApiResult.Success) {
      return;
    }

    if (
      serverResult === ESuiteApiResult.InvalidLogin ||
      serverResult === ESuiteApiResult.BadRequest ||
      serverResult === ESuiteApiResult.InvalidToken ||
      serverResult === ESuiteApiResult.NotValid
    ) {
      throw new ApiError('server', 'invalidResetLink', {
        serverResult,
      });
    }

    if (serverResult === ESuiteApiResult.InvalidPassword) {
      throw new ApiError('server', 'weakPassword', {
        serverResult,
      });
    }

    throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), {
      serverResult,
    });
  }
}
